import React, { useState } from 'react';
import { Card, Input, Button } from '../../components/ui';
import { Printer, FileText } from 'lucide-react';

interface PdfConfig {
  title: string;
  fields: { id: string; label: string; placeholder?: string; type?: string }[];
  renderTemplate: (data: Record<string, string>) => React.ReactNode;
}

export function GenericPdfGeneratorTool({ slug }: { slug: string }) {
  const [formData, setFormData] = useState<Record<string, string>>({});

  const handleInputChange = (id: string, value: string) => {
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handlePrint = () => {
    window.print();
  };

  // Pre-defined layouts for specific high-value tools
  const getCustomConfig = (slug: string): PdfConfig | null => {
    if (slug === 'silent-auction-bid-sheet') {
      return {
        title: 'Silent Auction Bid Sheet',
        fields: [
          { id: 'orgName', label: 'Organization Name', placeholder: 'e.g. Local Charity Foundation' },
          { id: 'itemName', label: 'Item Name', placeholder: 'e.g. Weekend Getaway' },
          { id: 'startingBid', label: 'Starting Bid ($)', type: 'number', placeholder: '50' },
          { id: 'bidIncrement', label: 'Minimum Increment ($)', type: 'number', placeholder: '10' }
        ],
        renderTemplate: (data) => (
          <div className="p-8 border-2 border-black max-w-3xl mx-auto h-[1000px] bg-white text-black">
            <h2 className="text-3xl font-bold text-center mb-2">{data.orgName || 'Organization Name'}</h2>
            <h3 className="text-2xl text-center mb-6">Silent Auction: {data.itemName || 'Item Name'}</h3>
            <div className="flex justify-between mb-8 border-b border-gray-400 pb-4">
              <span className="font-bold text-lg">Starting Bid: ${data.startingBid || '___'}</span>
              <span className="font-bold text-lg">Min Increment: ${data.bidIncrement || '___'}</span>
            </div>
            <table className="w-full text-left border-collapse border border-black">
              <thead>
                <tr>
                  <th className="border border-black p-3 w-1/3 bg-gray-100">Name / Bidder #</th>
                  <th className="border border-black p-3 w-1/3 bg-gray-100">Phone Number</th>
                  <th className="border border-black p-3 w-1/3 bg-gray-100">Bid Amount ($)</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 15 }).map((_, i) => (
                  <tr key={i}>
                    <td className="border border-black p-4"></td>
                    <td className="border border-black p-4"></td>
                    <td className="border border-black p-4"></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      };
    }
    
    if (slug === 'sign-up-sheet-generator' || slug === 'volunteer-sign-in-sheet') {
      return {
        title: slug === 'sign-up-sheet-generator' ? 'Sign-Up Sheet' : 'Volunteer Sign-In Sheet',
        fields: [
          { id: 'eventName', label: 'Event / Shift Name', placeholder: 'e.g. Fall Festival 2026' },
          { id: 'date', label: 'Date', placeholder: 'e.g. October 15th' },
        ],
        renderTemplate: (data) => (
          <div className="p-8 border-2 border-black max-w-3xl mx-auto h-[1000px] bg-white text-black">
            <h2 className="text-3xl font-bold text-center mb-2">{data.eventName || 'Event Sign-Up'}</h2>
            <h3 className="text-xl text-center mb-6">Date: {data.date || '___'}</h3>
            <table className="w-full text-left border-collapse border border-black mt-8">
              <thead>
                <tr>
                  <th className="border border-black p-3 w-1/12 text-center bg-gray-100">#</th>
                  <th className="border border-black p-3 w-4/12 bg-gray-100">Print Name</th>
                  <th className="border border-black p-3 w-4/12 bg-gray-100">Email Address</th>
                  <th className="border border-black p-3 w-3/12 bg-gray-100">Time / Shift</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 20 }).map((_, i) => (
                  <tr key={i}>
                    <td className="border border-black p-3 text-center">{i + 1}</td>
                    <td className="border border-black p-3"></td>
                    <td className="border border-black p-3"></td>
                    <td className="border border-black p-3"></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      };
    }
    
    if (slug === 'wifi-qr-poster') {
      return {
        title: 'Wi-Fi QR Code Poster',
        fields: [
          { id: 'orgName', label: 'Organization / Venue Name', placeholder: 'e.g. Community Center' },
          { id: 'ssid', label: 'Wi-Fi Network Name (SSID)', placeholder: 'Guest_Network' },
          { id: 'password', label: 'Wi-Fi Password', placeholder: 'supersecret' },
          { id: 'customText1', label: 'Additional Instructions', placeholder: 'Please accept the terms and conditions on the portal.' }
        ],
        renderTemplate: (data) => {
          const ssid = data.ssid || 'Guest_Network';
          const password = data.password || '';
          // Format for Wi-Fi QR Code: WIFI:T:WPA;S:mynetwork;P:mypass;;
          const qrData = `WIFI:T:WPA;S:${ssid};P:${password};;`;
          const qrUrl = `https://quickchart.io/qr?text=${encodeURIComponent(qrData)}&size=400`;

          return (
            <div className="p-10 border border-gray-200 max-w-[816px] mx-auto min-h-[1000px] bg-white text-black font-sans shadow-sm flex flex-col items-center">
              <h2 className="text-4xl font-black mb-2 mt-8 text-center">{data.orgName || 'Welcome'}</h2>
              <h3 className="text-xl font-bold mb-12 text-center text-gray-600">Connect to our Wi-Fi</h3>
              
              <div className="border-4 border-black p-8 rounded-xl bg-white shadow-lg flex flex-col items-center mb-12 w-full max-w-md">
                <img src={qrUrl} alt="Wi-Fi QR Code" className="w-64 h-64 mb-8" />
                
                <div className="w-full text-center space-y-4">
                  <div>
                    <p className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-1">Network Name (SSID)</p>
                    <p className="text-2xl font-black font-mono break-words">{ssid}</p>
                  </div>
                  {password && (
                    <div>
                      <p className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-1">Password</p>
                      <p className="text-2xl font-black font-mono break-words">{password}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {data.customText1 && (
                <div className="w-full max-w-lg text-center mt-auto mb-16">
                  <p className="text-lg leading-relaxed whitespace-pre-wrap">{data.customText1}</p>
                </div>
              )}
            </div>
          );
        }
      };
    }
    
    return null;
  };

  const getFallbackConfig = (slug: string): PdfConfig => {
    const formattedTitle = slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    return {
      title: formattedTitle,
      fields: [
        { id: 'title', label: 'Document Title', placeholder: formattedTitle },
        { id: 'orgName', label: 'Organization / Company Name', placeholder: 'Your Org Name' },
        { id: 'customText1', label: 'Primary Details / Subtitle', placeholder: 'Enter primary details here...' },
        { id: 'customText2', label: 'Additional Information', placeholder: 'Enter secondary details or rules...' }
      ],
      renderTemplate: (data) => (
        <div className="p-10 border border-gray-200 max-w-[816px] mx-auto min-h-[1000px] bg-white text-black font-sans shadow-sm">
          <h2 className="text-4xl font-black mb-4 text-center border-b-4 border-black pb-4">{data.title || formattedTitle}</h2>
          <h3 className="text-xl font-bold mb-8 text-center text-gray-600">{data.orgName || 'Your Organization'}</h3>
          
          <div className="mb-8 text-lg px-8">
            {data.customText1 ? <p className="mb-8 whitespace-pre-wrap leading-relaxed">{data.customText1}</p> : <div className="h-20 border-2 border-dashed border-gray-200 mb-8 rounded flex items-center justify-center text-gray-400">Primary Details Area</div>}
            {data.customText2 ? <p className="whitespace-pre-wrap leading-relaxed">{data.customText2}</p> : <div className="h-32 border-2 border-dashed border-gray-200 rounded flex items-center justify-center text-gray-400">Additional Information Area</div>}
          </div>
          
          <div className="mt-32 border-t-2 border-black pt-8 px-8 flex justify-between text-sm">
            <div>
              <p className="font-bold mb-2">Authorized Signature:</p>
              <div className="border-b-2 border-black mt-12 w-64"></div>
            </div>
            <div>
              <p className="font-bold mb-2">Date:</p>
              <div className="border-b-2 border-black mt-12 w-48"></div>
            </div>
          </div>
        </div>
      )
    };
  };

  const config = getCustomConfig(slug) || getFallbackConfig(slug);

  return (
    <div className="text-left">
      {/* SCREEN UI - Hidden during print */}
      <div className="print:hidden space-y-6">
        <div className="grid lg:grid-cols-12 gap-8">
          
          <div className="lg:col-span-4 space-y-6">
            <Card className="p-6 sticky top-24">
              <div className="flex items-center gap-3 mb-6">
                <FileText className="w-5 h-5 text-blue-500" />
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">Document Settings</h3>
              </div>
              
              <div className="space-y-4">
                {config.fields.map(field => (
                  <div key={field.id}>
                    <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">
                      {field.label}
                    </label>
                    <Input 
                      type={field.type || 'text'}
                      placeholder={field.placeholder}
                      value={formData[field.id] || ''} 
                      onChange={e => handleInputChange(field.id, e.target.value)} 
                    />
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700/50">
                <Button 
                  onClick={handlePrint} 
                  className="w-full gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold h-12"
                >
                  <Printer size={18} />
                  Print / Download PDF
                </Button>
                <p className="text-[10px] text-slate-500 text-center mt-3 uppercase tracking-wider">
                  Tip: Set Margins to "None" in Print Dialog
                </p>
              </div>
            </Card>
          </div>

          <div className="lg:col-span-8">
            <Card className="p-8 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 overflow-x-auto flex justify-center">
               {/* Live Preview Container */}
               <div className="origin-top scale-[0.6] sm:scale-[0.8] md:scale-[0.9] lg:scale-100 pointer-events-none select-none drop-shadow-2xl">
                 {config.renderTemplate(formData)}
               </div>
            </Card>
          </div>
          
        </div>
      </div>

      {/* PRINT UI - Only visible during print */}
      <div className="hidden print:flex print:absolute print:inset-0 print:m-0 print:p-0 bg-white z-[9999] justify-center items-start">
        <style type="text/css" media="print">
          {`
            @page { size: auto; margin: 0mm; }
            html, body { 
              color-scheme: light !important;
              background: white !important; 
              color: black !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              height: 100vh; 
              overflow: hidden !important; 
              margin: 0; 
              padding: 0; 
            }
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            #root > header, #root > footer { display: none !important; }
            ::-webkit-scrollbar { display: none; }
          `}
        </style>
        {config.renderTemplate(formData)}
      </div>
    </div>
  );
}
